import React, { useContext } from "react";
import moment from "moment-timezone";
import { faSignInAlt } from "@fortawesome/free-solid-svg-icons";

import {
	AlertContext,
	AddAlertFunction
} from "components/AlertProvider/AlertProvider";
import { OutputRow as MeetingsDataFlattened } from "api/getMeetingsFlattened";
import { OutputRow as MeetingRequestsData } from "api/getMeetingRequests";
import { OutputRow as MeetingRescheduleRequestsData } from "api/getMeetingRescheduleRequests";
import { ChatDetails } from "components/Chat/Chat";
import { useDefaultTimezone } from "hooks/useDefaultTimezone";
import terminology from "terminology.json";

import { isMeeting } from "./isMeeting";
import { isMeetingRescheduleRequest } from "./isMeetingRescheduleRequest";
import PurchasesTable from "components/PageDashboard/PurchasesTable";
import { CompatibleItem } from "components/PageDashboard/PurchasesTable/PurchasesTable";

type Event =
	| MeetingsDataFlattened
	| MeetingRequestsData
	| MeetingRescheduleRequestsData;

interface Props {
	isMentor: boolean | undefined;
	events: Event[] | undefined;
	uid: string | undefined;

	isWaiting: boolean;
	isLoading: boolean;

	onClickSignOff: (arg: MeetingsDataFlattened) => void;
	onClickMarkPaid: (arg: MeetingsDataFlattened) => void;
	onClickChat: (arg: ChatDetails | undefined) => void;
	onClickCancel: (arg: MeetingsDataFlattened) => void;
	onClickRequestReschedule: (arg: MeetingsDataFlattened) => void;
	onClickRespondToMeetingRescheduleRequest: (
		arg: MeetingRescheduleRequestsData
	) => void;
}

const cols = (isMentor: boolean) => [
	{ key: "startDate", heading: "Date" },
	{ key: "startTime", heading: "From" },
	{ key: "endTime", heading: "Until" },
	{ key: "timezone", heading: "Timezone" },
	{ key: "partner", heading: "With" },
	{ key: "medium", heading: "Using" },
	...(isMentor ? [{ key: "role", heading: "Role" }] : []),
	{ key: "status", heading: "Status" }
];

const MeetingsTable: React.FunctionComponent<Props> = ({
	isMentor,
	events,
	uid,
	isWaiting,
	isLoading,
	onClickSignOff,
	onClickMarkPaid,
	onClickChat,
	onClickCancel,
	onClickRequestReschedule,
	onClickRespondToMeetingRescheduleRequest
}) => {
	const [addAlert] = useContext(AlertContext);
	const defaultTimezone = useDefaultTimezone();

	const items: CompatibleItem[] | undefined = events
		? events.map(event => ({
				timezone: defaultTimezone,
				paymentStatus: undefined,

				// The following property is expected by PurchasesTable
				// TODO:WV:20251029:Confirm that this is the right way of handling it
				isAccepted: true,

				...event
		  }))
		: undefined;

	return (
		<PurchasesTable
			isMentor={isMentor}
			items={items as any}
			uid={uid}
			cols={cols(!!isMentor)}
			isWaiting={isWaiting}
			isLoading={isLoading}
			onClickMarkPaid={onClickMarkPaid}
			sortItems={(a, b) =>
				b.dateStartsUTC.getTime() - a.dateStartsUTC.getTime()
			}
			getColumnData={({ medium, dateStartsUTC, dateEndsJustBeforeUTC }) => {
				const start = moment(dateStartsUTC).tz(defaultTimezone);
				const endJustBefore = moment(dateEndsJustBeforeUTC).tz(defaultTimezone);

				return {
					medium,
					startDate: start.format("YYYY/MM/DD"),
					startTime: start.format("HH:mm"),
					endTime: endJustBefore.format("HH:mm")
				};
			}}
			getAction={(item: Event) => {
				if (isMeetingRescheduleRequest(item) && item.reschedulerUid !== uid) {
					return {
						onClick: () => onClickRespondToMeetingRescheduleRequest(item),
						label: "Respond"
					};
				}

				const endJustBefore = moment(item.dateEndsJustBeforeUTC).tz(
					defaultTimezone
				);
				if (item.guestUid === uid) {
					if (isMeeting(item)) {
						const { isSignedOffByGuest, isSignedOffBySystem } = item;
						const isSignedOff = !!isSignedOffByGuest || !!isSignedOffBySystem;
						const awaitingSignOff =
							!isSignedOff && endJustBefore.isBefore(new Date());
						if (awaitingSignOff) {
							return {
								onClick: () => onClickSignOff(item),
								label: "Sign off"
							};
						}
					}
				}

				if (isMeeting(item) && endJustBefore.isAfter(new Date())) {
					const isHost = item.hostUid === uid;
					const isGuest = !isHost;

					const { start, join } = getStartAndJoinButtons({
						meeting: item,
						isHost,
						isGuest,
						addAlert,
						includeIcon: false
					});

					if (start) {
						return start;
					}

					if (join) {
						return join;
					}
				}

				return undefined;
			}}
		/>
	);
};

export default MeetingsTable;

function getStartAndJoinButtons({
	meeting,
	isHost,
	isGuest,
	addAlert,
	includeIcon
}: {
	meeting: MeetingsDataFlattened;
	isHost: boolean;
	isGuest: boolean;
	addAlert: AddAlertFunction;
	includeIcon: boolean;
}) {
	const { startURL, joinURL, hostSkypeId, guestSkypeId } = meeting;

	const start = startURL
		? {
				onClick: () => window.open(startURL),
				label: "Start",
				icon: includeIcon ? faSignInAlt : undefined
		  }
		: undefined;

	const join = joinURL
		? {
				onClick: () => window.open(joinURL),
				label: "Join",
				icon: includeIcon ? faSignInAlt : undefined
		  }
		: (isGuest && hostSkypeId) || (isHost && guestSkypeId)
		? {
				onClick: () =>
					addAlert({
						title: `Join Skype ${terminology.meeting}`,
						contents: (
							<>
								Please open the Skype app and start a call with the Skype user{" "}
								<em>{isHost ? guestSkypeId : hostSkypeId}</em>.
							</>
						)
					}),
				label: "Join",
				icon: includeIcon ? faSignInAlt : undefined
		  }
		: undefined;

	return {
		...(start ? { start } : {}),
		...(join ? { join } : {})
	};
}
